import { Ticket, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/ui/Button";
import { cn, getCmsServiceFromList } from "~/lib/utils";
import { VoucherInfo } from "~/stores/bookingCart";
import type { ShopProduct } from "~/types/shop";

interface VoucherCardProps {
  voucher: VoucherInfo;
  rootData?: any;
  onRemove?: (code: string) => void;
  showRemoveButton?: boolean;
  isMatched?: boolean;
  i18n: any;
  mdCols?: number;
}

export default function VoucherCard({
  voucher,
  rootData,
  onRemove,
  showRemoveButton = false,
  isMatched = false,
  i18n,
  mdCols = 1,
}: VoucherCardProps) {
  const { t } = useTranslation();

  const expirationDate = voucher.voucherData.ExpirationDate
    ? new Date(voucher.voucherData.ExpirationDate)
    : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isExpiringSoon =
    expirationDate &&
    expirationDate >= today &&
    expirationDate <= new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

  const services: Array<{
    imageId: string | null;
    imageSrc: string | null;
    title: string;
    code: string;
    shortDescription?: string;
  }> = [];

  const giftCardType =
    voucher.giftCardType || voucher.voucherData.GiftCardType || "";
  const shopProducts = (rootData?.shopProducts || []) as ShopProduct[];

  if (giftCardType && shopProducts.length > 0) {
    const product = shopProducts.find((p) => p.sku === giftCardType);
    if (product) {
      const imageSrc = product.images?.[0]?.src ?? null;
      services.push({
        imageId: null,
        imageSrc,
        title: product.name,
        code: product.sku,
        shortDescription: product.short_description,
      });
    }
  }

  if (
    services.length === 0 &&
    voucher.voucherData.ServicesIncludedCodes &&
    voucher.voucherData.ServicesIncludedCodes.length > 0 &&
    rootData?.servicesData
  ) {
    voucher.voucherData.ServicesIncludedCodes.forEach((serviceCode) => {
      const cmsService = getCmsServiceFromList(
        rootData.servicesData,
        serviceCode,
      );
      if (cmsService) {
        const codeObj = cmsService.acf?.codes?.find(
          (c: any) => c.code === serviceCode,
        );
        const imageId = codeObj?.image?.id || cmsService.acf?.image?.id || null;
        services.push({
          imageId,
          imageSrc: null,
          title: cmsService.acf?.title || serviceCode,
          code: serviceCode,
          shortDescription: codeObj?.short_description,
        });
      } else {
        services.push({
          imageId: null,
          imageSrc: null,
          title: serviceCode,
          code: serviceCode,
        });
      }
    });
  }

  return (
    <div
      className={`relative rounded-xl overflow-hidden border-2 transition-all ${isMatched
          ? "border-green-500 bg-gradient-to-br from-green-50 to-white shadow-md"
          : "border-gray-200 bg-gradient-to-br from-gray-50 to-white shadow-sm hover:shadow-md"
        }`}
    >
      <div className="pt-4 px-4 pb-4 relative">
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2 flex-1">
            <div className="bg-secondary/10 rounded-lg">
              <Ticket className="h-5 w-5 text-secondary -mb-0.5" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-mono font-bold text-lg text-secondary block truncate leading-none">
                {voucher.code}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {showRemoveButton && onRemove && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRemove(voucher.code)}
                className="h-8 w-8 text-gray-500 hover:text-red-500 hover:bg-red-50 -mt-2 -mr-2"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {services.length > 0 && (
          <div className={cn("mb-3 mt-4 grid grid-cols-1 gap-3")}>
            {services.map((service, idx) => (
              <>
                <div key={idx} className="flex gap-3 items-start">
                  {service.imageSrc || service.imageId ? (
                    <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <img
                        src={
                          service.imageSrc || `/cms-asset/${service.imageId}`
                        }
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 w-16 h-16 rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center">
                      <Ticket className="h-8 w-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0 pt-1">
                    <h3 className="font-semibold text-sm text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap">
                      {service.title}
                    </h3>
                    {service.shortDescription && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: service.shortDescription,
                        }}
                        className="text-xs text-gray-600 line-clamp-2 leading-tight"
                      ></div>
                    )}
                  </div>
                </div>
              </>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          {!!voucher.voucherData.NrPaxInvolved && (
            <div className="flex items-center gap-2 text-xs text-gray-600 whitespace-nowrap">
              <span className="font-semibold">
                {t("Numero di ospiti inclusi")}:
              </span>
              <span className="bg-gray-100 px-2 py-0.5 rounded font-semibold">
                {voucher.voucherData.NrPaxInvolved}
              </span>
            </div>
          )}

          {expirationDate && (
            <div
              className={`inline-flex shrink-0 items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${expirationDate < today
                  ? "bg-red-100 text-red-800 border border-red-300"
                  : "bg-blue-100 text-blue-800 border border-blue-300"
                }`}
            >
              <span>
                {expirationDate < today ? t("Scaduto il") : t("Scade il")}
              </span>
              <span>
                {new Intl.DateTimeFormat(i18n.language, {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }).format(expirationDate)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
